import { faLayerGroup, faList } from '@fortawesome/pro-duotone-svg-icons';
import { faArrowUpFromSquare, faLocationCrosshairs, faMinus, faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AlertTitle, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, Link, Table, TableBody, TableCell, TableRow, Typography, styled, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ControlPosition, InfoWindow, Map, MapCameraChangedEvent, MapControl, MapMouseEvent, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import dayjs from 'dayjs';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useLocation, useParams } from 'react-router-dom';
import { BySubmitter, Marker, SiteEventType, Species, SpeciesDto, SpeciesSiteEventType, User } from '../api/dtos/dtos';
import { AppSettings } from '../AppSettings';
import RoadMapLayer from '../assets/images/map/layers/roadmap.png';
import SatelliteLayer from '../assets/images/map/layers/satellite.png';
import ChooseLocationBanner from '../components/banners/ChooseLocationBanner';
import Center from '../components/common/Center';
import Dropdown from '../components/common/Dropdown';
import Image from '../components/common/Image';
import { DEFAULT_GROUP_MEMBER_ID } from '../components/drawers/MapFilterDrawer';
import FilterButton from '../components/map/FilterButton';
import FormButton from '../components/map/FormButton';
import MapAddressSearch from '../components/map/MapAddressSearch';
import { SpeciesHeatmapConfiguration } from '../constants/species/Species';
import AuthContext from '../contexts/auth/AuthContext';
import MapContext, { MapEvent, MapFilter, MapFilterPeriodEnum, MapFilterSubmitter } from '../contexts/data/MapContext';
import MapFormContext from '../contexts/data/MapFormContext';
import { SpeciesContext } from '../contexts/data/SpeciesContext';
import SpeciesFormsContext from '../contexts/data/SpeciesFormsContext';
import UserGroupsContext from '../contexts/data/UserGroupsContext';
import isCurrentGroupAdmin from '../functions/user';
import useGetMapHeatmap from '../hooks/data/useGetMapHeatmap';
import useGetMapLegend from '../hooks/data/useGetMapLegend';
import useGetSiteEventGetMarkerContent from '../hooks/data/useGetSiteEventGetMarkerContent';
import usePostSiteEventMarkerCollection from '../hooks/data/usePostSiteEventMarkerCollection';
import usePostSiteEventMarkerContent from '../hooks/data/usePostSiteEventMarkerContent';
import useMapEvent from '../hooks/map/useMapEvent';
import AlertContext from '../contexts/styles/AlertContext';
import { ERROR_TIMEOUT, SUCCESS_TIMEOUT } from '../constants/styles/AlertConstants';
import isValidDateRange from '../functions/date';
import HeatMapLayer from '../components/map/HeatMapLayer';
import KmlLayer from '../components/map/KmlLayer';
import usePostObsoleteExternalRecord from '../hooks/data/usePostObsoleteExternalRecord';

const StyledIconButton = styled(IconButton)(() => ({
    width: '50px',
    height: '50px',
    backgroundColor: 'white',
    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',

    "&:hover": {
        backgroundColor: 'white',
        boxShadow: '0px 0px 6px 1px rgba(75,90,35,0.5)'
    },

    "&:hover svg": {
        color: 'black'
    }
}));

const ZoomWrapper = styled(Box)(() => ({
    width: '40px',
    height: '80px',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '5px',
    backgroundColor: 'white',
    justifyContent: 'space-around',
    overflow: 'hidden',
    color: 'grey'
}));

const StyledCenterZoom = styled(Center)(({ theme }) => ({
    height: '100%',

    "&:hover": {
        color: theme.palette.primary.main,
        cursor: 'pointer'
    }
}));

const StyledLegendTableCell = styled(TableCell)(() => ({
    padding: '5px 5px 5px 15px',
    border: 'none',

    "&:nth-of-type(2)": {
        paddingLeft: '0'
    }
}));

const StyledLayerButton = styled(Box)(() => ({
    padding: '10px',
    borderRadius: '5px',

    "&:hover": {
        cursor: 'pointer',
        backgroundColor: grey[200]
    }
}));

const CustomMap: FC = () => {
    //
    // -- Hooks --
    const theme = useTheme();
    const map = useMap();

    // 
    // -- Params --
    const { species: speciesName } = useParams();
    const location = useLocation();

    //
    // -- Contexts --
    const { user, openLogin } = useContext(AuthContext);
    const { species: { species, speciesDto } } = useContext(SpeciesContext);
    const { 
        filter, 
        mapCenter, 
        setMapCenter,
        setSubmitterFilter, 
        setPeriodFilter,
        setRecordTypeFilter,
        closeDrawer
    } = useContext(MapContext);
    const {
        drawerOpen: formDrawerOpen,
        openDrawer,
        maximiseDrawer,
        setViewHistoryRoot,
        setViewHistorySiteEventType,
        setMapFormData,
        mapFormData,
        setSelectingLocationCoords,
        selectingLocationCoords,
        recordToEdit,
        setRecordToEdit,
        setSiteEventType,
        subSpecies,
        setAccuracyWarning
    } = useContext(MapFormContext);
    const { 
        selectedForm, 
        canClickMap, 
        selectingLocation, 
        data: speciesForms 
    } = useContext(SpeciesFormsContext);
    const { data: userGroupsConfig } = useContext(UserGroupsContext);
    const { addAlert } = useContext(AlertContext);
    
    const queryParams = useMemo(() => {
        return Object.fromEntries(
            new URLSearchParams(location.search).entries()
        );
    }, [location.search]);
    
    useEffect(() => {
        if (!map) return;

        if (queryParams['zoom']) {
            map.setZoom(+queryParams['zoom']);
        }
        if (queryParams['lat'] && queryParams['long']) {
            const pos = {
                lat: +queryParams['lat'],
                lng: +queryParams['long'],
            };
            
            map.setCenter(pos);
        }
    }, [queryParams, map]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | undefined;

        if (!user && queryParams['groupid']) {
            openLogin();
        }
        else if (user && queryParams['groupid']) {
            const communityGroup = userGroupsConfig?.communityGroups?.find(
                x => x.communityGroupId == +queryParams['groupid']
            );

            if (communityGroup) {
                timeoutId = setTimeout(() => {
                    setSubmitterFilter(c => ({
                        ...c,
                        submitter: MapFilterSubmitter.MyGroup,
                        submitterLabel: communityGroup.name,
                        communityGroupId: communityGroup.communityGroupId,
                        communityGroupMemberId: DEFAULT_GROUP_MEMBER_ID
                    }));
                }, 1000); // 1 second (wait for defaults to be set so this submitter change isn't overwritten by defaults)
            }
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [user, queryParams, userGroupsConfig]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | undefined;
        if (queryParams['mapMode'] == 'rhdv' && species == Species.Rabbit) {
            timeoutId = setTimeout(() => {
                setRecordTypeFilter(c => ({
                    ...c,
                    sighting: false,
                    damage: false,
                    control: false,
                    disease: true,
                }));
                if (!isRegionAdmin && !isGroupAdmin && !isDataAccess && !isAdminOrSuperAdmin) {
                    setSubmitterFilter(c => ({
                        ...c,
                        submitter: MapFilterSubmitter.PublicData,
                        displayHeatmap: (speciesDto?.sensitive && !isEmailMode) ? true : false
                    }));
                }
            }, 1000); // 1 second (wait for defaults to be set so this submitter change isn't overwritten by defaults)
        }
        else if (queryParams['mapMode'] == 'default' && species == Species.Rabbit) {
            timeoutId = setTimeout(() => {
                setRecordTypeFilter(c => ({
                    ...c,
                    sighting: true,
                    damage: true,
                    control: true,
                    disease: false,
                }));
                if (!isRegionAdmin && !isGroupAdmin && !isDataAccess && !isAdminOrSuperAdmin) {
                    setSubmitterFilter(c => ({
                        ...c,
                        submitter: MapFilterSubmitter.PublicData,
                        displayHeatmap: true
                    }));
                }
            }, 1000); // 1 second (wait for defaults to be set so this submitter change isn't overwritten by defaults)
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [queryParams, species, speciesDto]);

    // 
    // -- States --
    const [position, setPosition] = useState({ latitude: -29, longitude: 135 });
    
    //
    // -- Cached --
    //
    // Param fields
    const isEmailMode = useMemo(() => {
        if (queryParams['viewMode'] == 'email') {
            return true;
        }
        return false;
    }, [queryParams]);
    
    // User fields
    const isAdminOrSuperAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'admin' || x === 'superadmin');
    }, [user]);
    
    const isDataAccess = useMemo(() => {
        return !!user?.roles?.find(x => x === 'dataaccess');
    }, [user]);

    const isRegionAdmin = useMemo(() => {
        return !!user?.roles?.find(x => x === 'RegionAdmin');
    }, [user]);
    
    const isDataAccessLimited = useMemo(() => {
        return !!user?.roles?.find(x => x === 'dataaccess_limited');
    }, [user]);

    const isDataAccessForThisSpecies = useMemo(() => {
        return (
            user?.matchingRegionAdminRoles?.some(
                x => x.speciesId == null || x.speciesId === species
            ) ?? false
        );
    }, [user?.matchingRegionAdminRoles, species]);

    const isGroupAdmin = useMemo(() => {
        return isCurrentGroupAdmin(userGroupsConfig, user, filter);
    }, [userGroupsConfig, user, filter, isCurrentGroupAdmin]);
    
    // Center the initial map camera onto "center" of AUS
    const defaultProps = useMemo(() => {
        return ({
            center: {
                lat: mapCenter?.latitude ?? -29,
                lng: mapCenter?.longitude ?? 135
            },
            zoom: mapCenter?.zoom ?? 5
        })
    }, [mapCenter]);

    // Load the map legend content.
    // If species is set to "None" or undefined, don't send the request.
    const legendParams = useMemo(() => {
        if (!species || species === Species.None) return undefined;

        return {
            species,
            rhdvMap: !!filter.recordType.disease
        };
    }, [species, filter.recordType.disease]);

    const { data: legend, loading: legendLoading } = useGetMapLegend(
        legendParams,
        { errorRetryCount: 0 }
    );
    
    useEffect(() => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position: GeolocationPosition) => {
                    if (position.coords.accuracy <= 100) {
                        setPosition({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        });
                        setAccuracyWarning(false);
                    }
                    else {
                        setAccuracyWarning(true);
                    }
                },
                () => {},
                {
                    enableHighAccuracy: true
                }
            );
        }
    }, []);

    // Cleanup states
    useEffect(() => {
        return () => {
            setMapCenter(undefined);
            closeDrawer();
        };
    }, []);

    // Get the map markers.
    // If species is set to "None" or undefined, don't send the request.
    const markersRequest = useMemo(() => {
        if (!species || species === Species.None) {
            return undefined;
        }

        if (!isValidDateRange(filter.reportingPeriod.fromField, filter.reportingPeriod.toField)) {
            return undefined;
        }

        // Record types
        const siteEventTypes = [];
        filter.recordType.sighting && siteEventTypes.push(SiteEventType.Sighting);
        filter.recordType.damage && siteEventTypes.push(SiteEventType.Damage);
        filter.recordType.control && siteEventTypes.push(SiteEventType.Control);
        filter.recordType.disease && siteEventTypes.push(SiteEventType.Disease);

        // Period
        let from = null;
        let to = null;
        switch (filter.reportingPeriod.period) {
            case MapFilterPeriodEnum.DateRange:
                from = filter.reportingPeriod.fromField;
                to = filter.reportingPeriod.toField;
                break;

            case MapFilterPeriodEnum.Year:
                const year = filter.reportingPeriod.yearField;
                from = year?.startOf('year');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (year?.year() === dayjs().year()) {
                    to = dayjs();
                } else {
                    to = year?.endOf('year');
                }

                break;

            case MapFilterPeriodEnum.MonthYear:
                const monthYear = filter.reportingPeriod.monthYearField;
                from = monthYear?.startOf('month');

                // If the year is equal to this year, we want to cap off
                // at the current date.
                if (monthYear?.year() === dayjs().year() && monthYear?.month() === dayjs().month()) {
                    to = dayjs();
                } else {
                    to = monthYear?.endOf('month');
                }

                break;

            case MapFilterPeriodEnum.LastSevenDays:
                from = dayjs().subtract(7, 'days');
                to = dayjs();
                break;

            case MapFilterPeriodEnum.LastThirtyOneDays:
                from = dayjs().subtract(31, 'days');
                to = dayjs();
                break;
        }

        let currentBySubmitter = BySubmitter.PublicData;
        if (filter.bySubmitter.submitter === MapFilterSubmitter.AllData) {
            currentBySubmitter = BySubmitter.AllData;
        }
        else if (filter.bySubmitter.submitter === MapFilterSubmitter.MyData) {
            currentBySubmitter = BySubmitter.MyData;
        }
        else if (filter.bySubmitter.submitter === MapFilterSubmitter.MyGroup) {
            currentBySubmitter = BySubmitter.MyGroup;
        }

        return {
            species: species,
            siteEventTypes: siteEventTypes,
            startDateOnOrAfter: from?.format('DD/MM/YYYY'),
            startDateOnOrBefore: to?.format('DD/MM/YYYY'),
            bySubmitter: currentBySubmitter,
            groupId: filter.bySubmitter.communityGroupId,
            groupMemberId: filter.bySubmitter.communityGroupMemberId,
            locatedInRegionIds: filter.bySubmitter.locatedInRegionIds,
            dataAccessId: filter.bySubmitter.dataAccessId,
            subSpeciesId: filter.recordType?.subSpeciesId == -1 ? undefined : filter.recordType?.subSpeciesId,
            showExternallySourcedData: filter.recordType?.externallySourcedData == true ? filter.recordType?.externallySourcedData : undefined 
        };
    }, [user, filter, species]);
    const { data: siteEventMarkers, loading: siteEventMarkersLoading, refresh: siteEventMarkerRefresh } = usePostSiteEventMarkerCollection(markersRequest, { revalidateOnFocus: false, errorRetryCount: 0 });

    useMapEvent(MapEvent.Refresh, async () => {
        await siteEventMarkerRefresh();
    })

    // Get the heatmap.
    // If species is set to "None" or undefined, don't send the request.
    const { data: heatmap } = useGetMapHeatmap(
        species && species !== Species.None ?
            { species: species } :
            undefined,
        {
            revalidateOnFocus: false,
            errorRetryCount: 0
        }
    );

    const [zoom, setZoom] = useState<number | null>(null);
    const [mapTypeId, setMapTypeId] = useState<"roadmap" | "satellite">("roadmap");
    const [selectedMarker, setSelectedMarker] = useState<{ marker: Marker, mapMarker: google.maps.Marker }>();
    const [buttonStates, setButtonStates] = useState<{ legend: boolean, layer: boolean }>({
        legend: false,
        layer: false
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteExternalRecordId, setDeleteExternalRecordId] = useState<number | undefined>(undefined);
    const [pendingDeleteId, setPendingDeleteId] = useState<number | undefined>(undefined);

    const obsoleteExternalRecord = useMemo(() => {
        if (!deleteExternalRecordId || !isAdminOrSuperAdmin) return undefined;

        return {
            id: deleteExternalRecordId
        }
    }, [deleteExternalRecordId]);
    const { data: obsoleteExternalRecordData, loading: obsoletingExternalRecord } = usePostObsoleteExternalRecord(obsoleteExternalRecord, { revalidateOnFocus: false, errorRetryCount: 0 });

    useEffect(() => {
        if (obsoleteExternalRecordData) {
            setDeleteDialogOpen(false);
            setDeleteExternalRecordId(undefined);
            setPendingDeleteId(undefined);
            siteEventMarkerRefresh();
        }
    }, [obsoleteExternalRecordData]);

    const showHeatmap = useMemo(() => {
        if (!map) return false;

        if (isEmailMode) return false;

        if (selectingLocation || formDrawerOpen) return false;

        return filter.bySubmitter.submitter === MapFilterSubmitter.PublicData &&
            filter.bySubmitter.displayHeatmap && 
            (speciesDto?.sensitive != true ? SpeciesHeatmapConfiguration[species].zoomCutoff > (zoom ?? 6) : true);
    }, [
        zoom, 
        filter, 
        selectingLocation, 
        formDrawerOpen,
        isDataAccessForThisSpecies,
        speciesDto,
        isEmailMode
    ]);

    useEffect(() => {
        if (showHeatmap) { 
            removeAllMarkers(); 
        }
    }, [showHeatmap]);

    const getMarkerImage = (speciesStr?: string, subSpeciesStr?: string) => {
        if (subSpeciesStr) {
            return `${AppSettings.baseUrl}/images/map/newMarker.${speciesStr?.replace("scan", "").replace("alert", "")}.${subSpeciesStr}.png`;
        }
        return `${AppSettings.baseUrl}/images/map/newMarker.${speciesStr?.replace("scan", "").replace("alert", "")}.png`;
    };

    const getSiteEventType = (type?: string) => {
        switch(type) {
            case "Sighting":
                return SiteEventType.Sighting;
            case "Damage":
                return SiteEventType.Damage;
            case "Control":
                return SiteEventType.Control;
            case "Disease":
                return SiteEventType.Disease;
            default:
                return SiteEventType.Sighting;
        }
    };

    const markerContentRequest = useMemo(() => {
        return {
            species: species,
            siteEventType: selectedMarker?.marker.siteEventType,
            id: selectedMarker?.marker.id,
            accessToken: queryParams['accessToken'] ? queryParams['accessToken'] : undefined 
        }
    }, [species, selectedMarker, queryParams]);

    const { data: selectedMarkerContent } = usePostSiteEventMarkerContent(markerContentRequest, { revalidateOnFocus: false, errorRetryCount: 0 });

    const paramMarkerContentRequest = useMemo(() => {
        if (queryParams['id'] && queryParams['type']) {
            return {
                species: species,
                siteEventType: getSiteEventType(queryParams['type']),
                id: +queryParams['id'],
                accessToken: queryParams['accessToken'],
                viewMode: queryParams['viewMode']
            }
        }
        return undefined;
    }, [species, selectedMarker, queryParams]);

    const { data: requestedMarkerContent } = useGetSiteEventGetMarkerContent(paramMarkerContentRequest, { revalidateOnFocus: false, errorRetryCount: 0 });

    const markerLibrary = useMapsLibrary('marker');

    // Ref to hold all markers by key
    const markersRef = useRef<{ [key: string]: google.maps.Marker }>({});

    // Add or replace a marker
    const setMarker = (key: string, marker: google.maps.Marker) => {
        if (markersRef.current[key]) {
            markersRef.current[key].setMap(null);
            google.maps.event.clearInstanceListeners(markersRef.current[key]);
        }
        markersRef.current[key] = marker;
    };

    // Remove a marker by key
    const removeMarker = (key: string) => {
        if (markersRef.current[key]) {
            markersRef.current[key].setMap(null);
            google.maps.event.clearInstanceListeners(markersRef.current[key]);
            delete markersRef.current[key];
        }
    };

    // Remove all markers from the map
    const removeAllMarkers = () => {
        Object.values(markersRef.current).forEach(marker => {
            marker.setMap(null);
            google.maps.event.clearInstanceListeners(marker);
        });
        markersRef.current = {};
    };

    // Requested Marker
    useEffect(() => {
        if (!map || !markerLibrary || !requestedMarkerContent?.marker) return;

        const key = "requestedMarker";
        const isEditingRequestedMarker =
            recordToEdit?.recordId === requestedMarkerContent.siteEvent.id &&
            recordToEdit?.siteEventType === requestedMarkerContent.marker.siteEventType;

        if (isEditingRequestedMarker) {
            removeMarker(key);
            setSelectedMarker(undefined);
            return;
        }

        const position = {
            lat: requestedMarkerContent.marker.latitude!,
            lng: requestedMarkerContent.marker.longitude!
        };

        let marker = markersRef.current[key];

        if (!marker) {
            marker = new markerLibrary.Marker({
                map,
                position,
                icon: { url: getMarkerImage(speciesName, subSpecies), anchor: new google.maps.Point(10, 80) },
                clickable: !formDrawerOpen,
                zIndex: 1000,
            });

            marker.addListener("click", (e: any) => {
                e.domEvent.preventDefault();
                setSelectedMarker({ marker: requestedMarkerContent.marker, mapMarker: marker! });
            });

            setMarker(key, marker);
        } else {
            marker.setPosition(position);
        }

        setSelectedMarker({ marker: requestedMarkerContent.marker, mapMarker: marker });
        setSubmitterFilter(c => ({ ...c, submitterLabel: "ID " + requestedMarkerContent.siteEvent.id }));
        setPeriodFilter(c => ({ ...c, periodLabel: "Specific " + queryParams['type'] + " Record" }));

    }, [map, markerLibrary, requestedMarkerContent, formDrawerOpen, recordToEdit]);

    // Selecting Location Marker
    useEffect(() => {
        if (!map || !markerLibrary || showHeatmap || !selectingLocation) return;
        const key = "selectingLocation";
        const markerPosition = selectingLocationCoords ?? { lat: position.latitude, lng: position.longitude };

        let marker = markersRef.current[key];

        if (!marker) {
            marker = new markerLibrary.Marker({
                map,
                position: markerPosition,
                icon: { url: getMarkerImage(speciesName, subSpecies), anchor: new google.maps.Point(10, 80) },
                draggable: true,
                clickable: false,
                zIndex: 999,
            });

            marker.addListener("dragend", (e: any) => {
                setSelectingLocationCoords({ lat: e.latLng.lat(), lng: e.latLng.lng() });
            });

            setMarker(key, marker);
        } else {
            marker.setPosition(markerPosition);
        }

    }, [map, markerLibrary, showHeatmap, selectingLocation, selectingLocationCoords, position.latitude, position.longitude]);

    // Clear lat lng marker on form change
    useEffect(() => {
        if (selectedForm?.title != 'Sighting') removeMarker("selectedForm-Sighting");
        if (selectedForm?.title != 'Damage') removeMarker("selectedForm-Damage");
        if (selectedForm?.title != 'Control') removeMarker("selectedForm-Control");
        if (selectedForm?.title != 'Disease') removeMarker("selectedForm-Disease");
    }, [selectedForm?.title]);

    // Selected Form Marker
    const [lat, lng] = useMemo(() => {
        const coords = mapFormData?.[selectedForm?.name ?? ""];
        return [coords?.Latitude, coords?.Longitude];
    }, [mapFormData, selectedForm?.name]);

    useEffect(() => {
        if (!map || !markerLibrary || showHeatmap || !selectedForm || selectingLocation) return;
        if ((isNaN(+lat) && isNaN(+lng)) && position.latitude === -29 && position.longitude === 135) return;

        const key = `selectedForm-${selectedForm.title}`;
        const markerPosition = lat && lng ? { lat: +lat, lng: +lng } : { lat: position.latitude, lng: position.longitude };

        let marker = markersRef.current[key];

        if (!marker) {
            marker = new markerLibrary.Marker({
                map,
                position: markerPosition,
                icon: { url: getMarkerImage(speciesName, subSpecies), anchor: new google.maps.Point(10, 80) },
                draggable: true,
                clickable: false,
                zIndex: 999,
            });

            marker.addListener("dragend", (e: any) => {
                setMapFormData(prev => ({
                    ...prev,
                    [selectedForm.name]: {
                        ...prev?.[selectedForm.name],
                        Latitude: e.latLng.lat().toString(),
                        Longitude: e.latLng.lng().toString(),
                        manuallyEdited: true,
                    }
                }));
            });

            setMarker(key, marker);
        } else {
            marker.setPosition(markerPosition);
        }

    }, [map, markerLibrary, showHeatmap, selectedForm, selectingLocation, position.latitude, position.longitude, lat, lng]);

    // Site Event Markers
    useEffect(() => {
        if (!map || !markerLibrary || showHeatmap || requestedMarkerContent?.marker) return;

        removeAllMarkers();

        if (!siteEventMarkers?.markers?.length) return;

        const siteMarkers: google.maps.Marker[] = [];
        let zIndex = 1;

        for (const m of siteEventMarkers.markers) {
            if (!m.latitude || !m.longitude) continue;
            if (recordToEdit?.recordId === m.id && recordToEdit?.siteEventType === m.siteEventType) continue;
            const key = `siteEvent-${m.id}-${m.speciesSiteEventType}`;

            let marker = markersRef.current[key];

            if (!marker) {
            marker = new markerLibrary.Marker({
                map,
                position: { lat: m.latitude, lng: m.longitude },
                icon: { url: `${AppSettings.baseUrl}/images/map/markers/${m.icon}` },
                clickable: !formDrawerOpen,
                zIndex: zIndex++
            });

            marker.addListener("click", (e: any) => {
                e.domEvent.preventDefault();
                setSelectedMarker({ marker: m, mapMarker: marker! });
            });

            setMarker(key, marker);
            } else {
                marker.setPosition({ lat: m.latitude, lng: m.longitude });
            }

            siteMarkers.push(marker);
        }

        return () => {
            siteMarkers.forEach(m =>
                removeMarker(`siteEvent-${m.get("id")}-${m.get("speciesSiteEventType")}`)
            );
        };

    }, [map, markerLibrary, showHeatmap, siteEventMarkers, formDrawerOpen, recordToEdit]);

    const handleGeoLocationClick = useCallback(() => {
        // If the navigator and map both have been intialised
        if (navigator.geolocation && map) {
            navigator.geolocation.getCurrentPosition(
                (position: GeolocationPosition) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };

                    // TODO: Set marker dictating the user's geo location

                    map.setCenter(pos);
                    map.setZoom(16);
                },
                () => {
                    // TODO: Handle geo nav error
                },
                {
                    enableHighAccuracy: true
                }
            );

            // Else, notify user that this is not working
        } else {
            // TODO: Handle navigator error
        }
    }, [map]);

    const handleMapClick = useCallback((event: MapMouseEvent) => {
        if (event.type === 'click') {
            if (canClickMap) {
                // Save lat and lng to provider
                setMapFormData({
                    ...mapFormData,
                    [selectedForm!.name]: {
                        ...mapFormData?.[selectedForm!.name],
                        "Latitude": event.detail.latLng?.lat.toString(),
                        "Longitude": event.detail.latLng?.lng.toString()
                    }
                });
            }
            if (selectingLocation) {
                // Temp store lat and lng to provider
                setSelectingLocationCoords({
                    lat: Number(event.detail.latLng?.lat.toString()),
                    lng: Number(event.detail.latLng?.lng.toString())
                });
            }
        }
    }, [selectedForm, setMapFormData, mapFormData, canClickMap, selectingLocation, setSelectingLocationCoords]);

    const zoomIn = useCallback(() => {
        if (map) {
            map.setZoom((map.getZoom() ?? defaultProps.zoom) + 1);
        }
    }, [map]);

    const zoomOut = useCallback(() => {
        if (map) {
            map.setZoom((map.getZoom() ?? defaultProps.zoom) - 1);
        }
    }, [map]);

    const getImageLink = (galleryId: number | undefined, photoId: number | undefined, thumbnail: boolean, accessToken?: string) => {
        if (thumbnail) {
            return `${AppSettings.baseUrl}/gallery/${galleryId}/photo/${photoId}/thumbnail${accessToken ? '?accessToken=' + accessToken : ''}`;
        }
        return `${AppSettings.baseUrl}/gallery/${galleryId}/photo/${photoId}${accessToken ? '?accessToken=' + accessToken : ''}`;
    };

    const getViewNotificationsLink = (siteEventId: number | undefined, species: Species, speciesSiteEventType: SpeciesSiteEventType) => {
        return `${AppSettings.adminBaseUrl}${AppSettings.adminMarkerNotificationPath}?id=${siteEventId}&speciesId=${species}&sightingTypeId=${speciesSiteEventType}`;
    };

    const handleEditRecordClick = (siteEventType?: SiteEventType, recordId?: number, hasImage?: boolean) => {
        if (!siteEventType || recordId == null) return;

        const requested = requestedMarkerContent;
        const requestedSiteEvent =
            requested?.marker &&
            requested.siteEvent?.id === recordId &&
            requested.marker.siteEventType === siteEventType
                ? requested.siteEvent
                : undefined;

        const siteEvent =
            requestedSiteEvent ??
            siteEventMarkers?.markers?.find(
                x => x.siteEventType === siteEventType && x.id === recordId
            );

        const selectedForm = speciesForms?.find(
            x => x.siteEventType === siteEventType
        );

        if (!siteEvent || !selectedForm) return;

        selectedMarker?.mapMarker.setMap(null);
        setSelectedMarker(undefined);

        setRecordToEdit({ siteEventType, recordId, hasImage });
        setSiteEventType(siteEventType);

        setMapFormData(prev => ({
            ...prev,
            [selectedForm.name]: {
                ...prev?.[selectedForm.name],
                Latitude: siteEvent.latitude?.toString(),
                Longitude: siteEvent.longitude?.toString()
            }
        }));

        openDrawer();
        maximiseDrawer();
    };

    const getMaxZoom = (species: Species, user: User | null, filter: MapFilter, speciesDto?: SpeciesDto) => {
        if (isAdminOrSuperAdmin || isDataAccessForThisSpecies || isEmailMode || formDrawerOpen) {
            return 20;
        }

        if (filter.bySubmitter.submitter == MapFilterSubmitter.PublicData && speciesDto?.sensitive == true) {
            return 5;
        }

        switch (species) {
            case Species.Fox:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 14;
                }
                return 20;
            case Species.Deer:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 5;
                }
                return 20;
            case Species.Mouse:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 15;
                }
                return 20;
            case Species.FeralGoat:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 6;
                }
                return 20;
            case Species.FeralPig:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 5;
                }
                return 20;
            case Species.Rabbit:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 13;
                }
                return 20;
            case Species.Myna:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 16;
                }
                return 20;
            case Species.WildDog:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 6;
                }
                return 20;
            case Species.FeralCat:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 14;
                }
                return 20;
            case Species.Starling:
            case Species.FeralFish:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 18;
                }
                return 20;
            case Species.Toad:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 14;
                }
                return 20;
            default:
                if (!user || filter.bySubmitter.submitter == MapFilterSubmitter.PublicData) {
                    return 9;
                }
                return 20;
        }
    }

    const handleShareLinkClick = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            addAlert({
                content: <AlertTitle>
                    Copied share link.
                </AlertTitle>,
                dismissable: true,
                severity: 'success',
                timeout: SUCCESS_TIMEOUT
            });
        } catch (err) {
            addAlert({
                content: <AlertTitle>
                    Failed to copy share link.
                </AlertTitle>,
                dismissable: true,
                severity: 'error',
                timeout: ERROR_TIMEOUT
            });
            console.error(err);
        }
    };

    return (

        <Map
            mapId={"feral-scan-map"}
            mapTypeId={mapTypeId}
            /* -- Default UI Controls -- */
            zoomControl={false}
            mapTypeControl={false}
            fullscreenControl={false}
            streetViewControl={false}
            maxZoom={getMaxZoom(species, user, filter, speciesDto)}
            /* -- Default Map View -- */
            defaultCenter={defaultProps.center}
            defaultZoom={defaultProps.zoom}
            /* -- Map Events -- */
            onCameraChanged={(ev: MapCameraChangedEvent) => {
                setZoom(ev.detail.zoom);
            }}
            onClick={(ev: MapMouseEvent) => handleMapClick(ev)}
        >
            {/* -- Heatmap -- */}
            <HeatMapLayer show={showHeatmap} data={heatmap} />

            {/* -- KMLs -- */}
            <KmlLayer show />

            {/* -- Marker Popup -- */}
            {
                !!selectedMarker && (
                    <InfoWindow
                        headerDisabled={true}
                        style={{ 
                            minWidth: '200px', 
                            maxWidth: '250px', 
                            maxHeight: '400px', 
                            overflowY: 'scroll', 
                            scrollbarWidth: 'none'
                        }}
                        anchor={selectedMarker.mapMarker}
                        onClose={() => setSelectedMarker(undefined)}
                    >
                        <Box sx={{ position: 'relative', paddingBottom: 1 }}>
                            {selectedMarkerContent?.content?.shareLink && (
                                <IconButton
                                    sx={{
                                        position: 'absolute',
                                        top: -5,
                                        right: 20,
                                        fontSize: '1rem',
                                        marginRight: '5px',
                                        zIndex: 1,
                                    }}
                                    onClick={() => handleShareLinkClick(selectedMarkerContent.content.shareLink ?? "")}
                                >
                                    <FontAwesomeIcon icon={faArrowUpFromSquare} />
                                </IconButton>
                            )}
                            <IconButton
                                sx={{
                                    position: 'absolute',
                                    top: -5,
                                    right: 0,
                                    fontSize: '1rem',
                                    zIndex: 1,
                                }}
                                onClick={() => setSelectedMarker(undefined)}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </IconButton>
                        </Box>
                        <Box sx={{ m: 2 }}/>
                        { parse(DOMPurify.sanitize(selectedMarkerContent?.content.content ?? "", { ADD_ATTR: ['target'] })) }
                        <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: '10px', paddingBottom: '10px' }}>
                            {selectedMarkerContent?.content.canEditRecord && (
                                <>
                                    <Link
                                        sx={{ cursor: 'pointer', my: '2px' }}
                                        onClick={() =>
                                            handleEditRecordClick(
                                                selectedMarkerContent.content.siteEventType,
                                                selectedMarker.marker.id,
                                                selectedMarkerContent.content.galleryPhotoId != null,
                                            )}
                                    >
                                        Edit Record
                                    </Link>
                                    {selectedMarkerContent?.content.parentId && (
                                        <Link
                                            sx={{ cursor: 'pointer', my: '2px' }}
                                            onClick={() => {
                                                setViewHistoryRoot(selectedMarkerContent?.content.id);
                                                setViewHistorySiteEventType(selectedMarker.marker.siteEventType);
                                                openDrawer();
                                                maximiseDrawer();
                                            }}
                                        >
                                            View History
                                        </Link>
                                    )}
                                </>
                            )}
                            {(isAdminOrSuperAdmin && selectedMarkerContent?.content.siteEventType == SiteEventType.External) && (
                                <Link
                                    component="button"
                                    onClick={() => {
                                        setPendingDeleteId(selectedMarkerContent.content.id);
                                        setDeleteDialogOpen(true);
                                    }}
                                >
                                    Delete Record
                                </Link>
                            )}
                            {(user && user?.roles?.includes('superadmin') && selectedMarkerContent?.content.speciesSiteEventType) && (
                                <Link
                                    href={getViewNotificationsLink(selectedMarker.marker.id, species, selectedMarkerContent?.content.speciesSiteEventType)}
                                    target="_blank"
                                    sx={{ cursor: 'pointer', my: '2px' }}
                                >
                                    View Notifications
                                </Link>
                            )}

                            {((selectedMarkerContent?.content.canSeeProtectedFields || 
                                selectedMarkerContent?.content?.canViewPhoto) &&
                                selectedMarkerContent?.content.galleryId !== undefined &&
                                selectedMarkerContent?.content.galleryPhotoId !== undefined) && (
                                    <>
                                        <Box height="20px" />
                                        <Link
                                            href={getImageLink(selectedMarkerContent?.content.galleryId, selectedMarkerContent?.content.galleryPhotoId, false, queryParams['accessToken'])}
                                            target="_blank"
                                            sx={{ cursor: 'pointer', my: '2px' }}
                                        >
                                            Click to enlarge
                                        </Link>
                                        <Box
                                            component="img"
                                            sx={{ width: 'auto', height: 'auto' }}
                                            alt="Failed to load image."
                                            src={getImageLink(selectedMarkerContent?.content.galleryId, selectedMarkerContent?.content.galleryPhotoId, false, queryParams['accessToken'])}
                                        />
                                    </>
                            )}
                        </Box>
                    </InfoWindow>
                )
            }


            <MapControl position={ControlPosition.TOP_LEFT}>
                <Box
                    sx={{
                        margin: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                        alignItems: "flex-start",
                    }}
                >
                    {/* Form Button */}
                    {!selectingLocation && !isDataAccess && !isDataAccessLimited && !isGroupAdmin && (
                        <FormButton />
                    )}

                    {/* Search Places */}
                    <MapAddressSearch
                        onPlaceSelected={(lat, lng) => {
                            if (map) {
                                map.setCenter(new google.maps.LatLng(lat, lng));
                                map.setZoom(18);
                            }
                        }}
                    />
                </Box>
            </MapControl>

            {/* -- Map Legend and Layer Views -- */}
            <MapControl position={ControlPosition.RIGHT_TOP}>
                <Box
                    sx={{
                        margin: '16px 16px 16px 0',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'end',
                        width: '0px',
                    }}
                >
                    {/* -- Map Filter -- */}
                    {
                        !selectingLocation ?
                            <FilterButton
                                loading={siteEventMarkersLoading}
                                disabled={formDrawerOpen}
                            /> :
                            <Box height="85px"></Box>
                    }

                    {/* -- Map Layer -- */}
                    <StyledIconButton
                        sx={{
                            width: '40px',
                            height: '40px',
                            marginBottom: '16px',
                            color: buttonStates.layer ? theme.palette.primary.main : 'normal',
                            borderRadius: buttonStates.layer ? '0 50% 50% 0' : '50%',

                            "&:hover svg": {
                                color: buttonStates.layer ? theme.palette.primary.main : 'black'
                            }
                        }}
                        onClick={() => setButtonStates(c => ({ legend: false, layer: !c.layer }))}
                    >
                        <FontAwesomeIcon fontSize='1.3rem' icon={faLayerGroup} />
                        <Dropdown
                            anchor={null}
                            open={buttonStates.layer}
                            position='left'
                            sx={{ borderRadius: '5px 0 5px 5px' }}
                        >
                            <Box>
                                <StyledLayerButton
                                    onClick={() => {
                                        setMapTypeId("roadmap");
                                        setButtonStates({
                                            legend: false,
                                            layer: false,
                                        });
                                    }}
                                >
                                    <Image width='100px' height='100px' src={RoadMapLayer} />
                                    <Typography color="#333">Default</Typography>
                                </StyledLayerButton>
                                <Divider />
                                <StyledLayerButton
                                    onClick={() => {
                                        setMapTypeId("satellite");
                                        setButtonStates({
                                            legend: false,
                                            layer: false,
                                        });
                                    }}
                                >
                                    <Image width='100px' height='100px' src={SatelliteLayer} />
                                    <Typography color="#333">Satellite</Typography>
                                </StyledLayerButton>
                            </Box>
                        </Dropdown>
                    </StyledIconButton>

                    {/* -- Map Legend -- */}
                    {!selectingLocation && (
                        <StyledIconButton
                            sx={{
                                width: '40px',
                                height: '40px',
                                marginBottom: '16px',
                                color: buttonStates.legend ? theme.palette.primary.main : 'normal',
                                borderRadius: buttonStates.legend ? '0 50% 50% 0' : '50%',

                                "&:hover svg": {
                                    color: buttonStates.legend ? theme.palette.primary.main : 'black'
                                }
                            }}
                            onClick={() => setButtonStates(c => ({ layer: false, legend: !c.legend }))}
                        >
                            <Center>
                                <FontAwesomeIcon fontSize='1.3rem' icon={faList} />
                                {
                                    legendLoading ?
                                        <CircularProgress
                                            sx={{
                                                color: theme.palette.primary.main,
                                                position: 'absolute',
                                                maxHeight: '40px',
                                                maxWidth: '40px'
                                            }}
                                        /> :
                                        null
                                }
                            </Center>
                            <Dropdown
                                anchor={null}
                                open={buttonStates.legend}
                                position='left'
                                sx={{ borderRadius: '5px 0 5px 5px' }}
                            >
                                {
                                    legend &&
                                    <Box>
                                        <Table sx={{ width: '250px', marginY: '10px', marginRight: '15px' }}>
                                            <TableBody>
                                                {
                                                    legend.filter(x => !x.title?.includes("Management")).map((x, idx) => (
                                                        <TableRow
                                                            key={`map-legend-${idx}`}
                                                        >
                                                            <StyledLegendTableCell width='16px'>
                                                                <Center>
                                                                    <Image src={x.imageUrl} />
                                                                </Center>
                                                            </StyledLegendTableCell>
                                                            <StyledLegendTableCell>
                                                                <Typography
                                                                    sx={{ color: '#5F2B10' }}
                                                                    fontWeight='bold'
                                                                    fontSize='12px'
                                                                >
                                                                    {x.title}
                                                                </Typography>
                                                            </StyledLegendTableCell>
                                                        </TableRow>
                                                    ))
                                                }
                                            </TableBody>
                                        </Table>
                                    </Box>
                                }
                            </Dropdown>
                        </StyledIconButton>
                    )}

                    {/* -- Map Zoom -- */}
                    <ZoomWrapper>
                        <StyledCenterZoom
                            onClick={() => zoomIn()}
                        >
                            <FontAwesomeIcon fontSize="1.2rem" icon={faPlus} />
                        </StyledCenterZoom>
                        <Divider />
                        <StyledCenterZoom
                            onClick={() => zoomOut()}
                        >
                            <FontAwesomeIcon fontSize="1.2rem" icon={faMinus} />
                        </StyledCenterZoom>
                    </ZoomWrapper>
                </Box>
            </MapControl>

            {/* -- User's Geo Location -- */}
            <MapControl position={ControlPosition.RIGHT_BOTTOM}>
                <Box margin='0 16px 16px 0'>
                    <StyledIconButton
                        onClick={handleGeoLocationClick}
                    >
                        <FontAwesomeIcon icon={faLocationCrosshairs} />
                    </StyledIconButton>
                </Box>
            </MapControl>

            {/* -- Choose location banner -- */}
            {selectingLocation && (
                <ChooseLocationBanner />
            )}

            {/** -- Delete external record confirmation -- */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle 
                    sx={{
                        color: 'black'
                    }}
                >
                    Delete Record
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to permanently delete this record?
                    </DialogContentText>
                </DialogContent>

                <DialogActions 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between' 
                    }}
                >
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        disabled={obsoletingExternalRecord}
                        onClick={() => {
                            if (pendingDeleteId) {
                                setDeleteExternalRecordId(pendingDeleteId);
                            }
                        }}
                    >
                        {obsoletingExternalRecord ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Map>
    );
}


const MapPage: FC = () => {

    return (
        <div style={{ flex: '1 1 auto', alignSelf: 'stretch' }}>
            <CustomMap />
        </div>
    )
}

export default MapPage;
